import { Box, Flex, useToken } from '@chakra-ui/react';
import {
  Heading,
  HStack,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { Button } from 'components/ui/button';
import { Info } from 'lucide-react';
import { FoodCard } from './FoodCard';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader } from '../../Loader';
import { getImage } from './util';
import { useRecommendedFoods } from '../../../api/healthCoach/useRecommendedFoods';
import { useUpdateUser } from '../../../api/healthCoach/mutations/useUpdateUser';
import { IconButton } from '../../Form/IconButton';
import { PencilIcon } from '../../../icons/PencilIcon';

export const RecommendedFoods = () => {
  const { patient } = useParams();
  const { mutation } = useUpdateUser(patient ?? '');
  const { error, foods, isLoading } = useRecommendedFoods(patient);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedFoods, setSelectedFoods] = useState<string[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const canAddFood = selectedFoods.length < 21;

  const foodsCount = foods?.length;
  const subTitle = `${foods?.length} food${
    foodsCount !== undefined && foodsCount > 1 ? 's' : ''
  }`;

  const handleFoodSelection = (foodId: string, checked: boolean) => {
    if (checked && canAddFood) {
      return setSelectedFoods([...selectedFoods, foodId]);
    }

    if (checked && !canAddFood) {
      setErrorMsg('You can only select 21 foods.');
      return;
    }

    const unCheckedFoodIndex = selectedFoods.indexOf(foodId);
    return setSelectedFoods([
      ...selectedFoods.slice(0, unCheckedFoodIndex),
      ...selectedFoods.slice(unCheckedFoodIndex + 1),
    ]);
  };

  useEffect(() => {
    if (!isLoading && !!error) {
      setErrorMsg(
        'Error while loading the recommended foods. Please try again later ot contact support.'
      );
    }
  }, [error, isLoading]);

  useEffect(() => {
    // reset error message
    if (!error && !!errorMsg && selectedFoods.length < 21) setErrorMsg('');
  }, [errorMsg, error, selectedFoods.length]);

  const [info600] = useToken('colors', ['info.600']);

  if (isLoading) {
    return (
      <Box p={0}>
        <Loader loading={true} />
      </Box>
    );
  }

  return (
    <VStack>
      <HStack alignItems={'center'}>
        <Heading fontWeight={'normal'} size={'md'} textAlign="left">
          Recommended Foods
        </Heading>

        {!isEditing && (
          <IconButton
            onClick={() => {
              setIsEditing(() => !isEditing);
            }}
          >
            <PencilIcon />
          </IconButton>
        )}

        {isEditing && (
          <HStack h={'24px'} alignItems={'center'}>
            <Button
              disabled={mutation.isLoading}
              h={'20px'}
              minH={'16px'}
              ml={2}
              onClick={() => {
                setIsEditing(false);
                setSelectedFoods([]);
              }}
              rounded={3}
              size="xs"
              variant={'outline'}
              w={'16px'}
            >
              Cancel
            </Button>

            <Button
              disabled={mutation.isLoading || selectedFoods.length !== 21}
              h={'20px'}
              loading={mutation.isLoading}
              minH={'16px'}
              ml={2}
              onClick={async () => {
                await mutation.mutateAsync({
                  recommended_foods: selectedFoods,
                });
                setIsEditing(false);
                setSelectedFoods([]);
              }}
              rounded={3}
              size="xs"
              w={'16px'}
            >
              Save
            </Button>
          </HStack>
        )}
      </HStack>

      <Text mb={1}>{subTitle}</Text>

      <Box h={'24px'}>
        {isEditing && !errorMsg && canAddFood && (
          <HStack alignItems={'center'}>
            <Info color={info600} size={16} style={{ marginRight: 4 }} />
            <Text>Select 21 foods to save</Text>
            {!!selectedFoods.length && (
              <Text>
                : <b>{selectedFoods.length}</b> out of <b>21</b> selected
              </Text>
            )}
          </HStack>
        )}

        {errorMsg && <Text color={'red.500'}>{errorMsg}</Text>}
      </Box>

      <Box>
        <Flex flexDir={'row'} overflow={'scroll'} pb={4}>
          {foods?.map(({ id, image_url, is_recommended_user_food, name }) => (
            <FoodCard
              active={is_recommended_user_food}
              disableSeverityLabel
              editing={isEditing}
              foodId={name}
              foodLabel={name}
              imgURL={getImage(image_url)}
              key={id}
              onChange={handleFoodSelection}
              selected={selectedFoods.includes(name)}
            />
          ))}
        </Flex>
      </Box>
    </VStack>
  );
};
